import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  CircularProgress
} from '@mui/material';
import {
  Home,
  Psychology,
  Dashboard,
  AdminPanelSettings
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';

export const LogManagementPage: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have developer_tools.view permission (after loading completes)
  if (!user?.permissions?.includes('developer_tools.view')) {
    return <Navigate to="/" replace />;
  }

  const logPages = [
    {
      title: 'LLM Activity',
      description: 'View and analyze LLM API call logs including prompts, responses, and usage metrics',
      icon: <Psychology fontSize="large" />,
      action: () => navigate('/admin/log-management/llm-activity'),
      color: '#9c27b0'
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Log Management</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Dashboard sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Log Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            View and analyze system logs and activity
          </Typography>
        </Box>

        {/* Log Pages */}
        <Grid container spacing={3}>
          {logPages.map((page) => (
            <Grid item xs={12} md={6} lg={4} key={page.title}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={page.action}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        color: page.color,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {page.icon}
                    </Box>
                    <Typography variant="h6">
                      {page.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {page.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};
